/**
 * Parsing prescription shorthand as it is actually written in India.
 *
 * Indian scripts read `1-0-1 A/F` (morning-noon-night, after food) or use the
 * Latin abbreviations `OD / BD / TDS / QID / HS / SOS`. Every US-designed app
 * makes the patient translate that into clock times by hand — and then gets it
 * wrong when the patient eats at 10am rather than 8am.
 *
 * We parse it directly and resolve it against the patient's own meal times, so
 * `1-0-1` means 08:30 and 20:30 for one person and 10:30 and 22:30 for another.
 *
 * The parser is deliberately tolerant: it searches inside a longer string, so
 * it can be pointed at a whole OCR'd line like
 * `TAB. GLYCOMET 500MG 1-0-1 A/F x 15 days`.
 *
 * Output is ALWAYS a draft for the user to confirm. Nothing here auto-creates
 * a medication.
 */

import type { FoodRelation, MealAnchor, MealKey } from './types.js';

export interface PrescriptionDraft {
  /** `prn` never alarms; `interval` is "every N hours"; otherwise meal-anchored. */
  type: 'meal_relative' | 'interval' | 'prn';
  anchors?: MealAnchor[];
  intervalHours?: number;
  /** Total daily quantity, for a sanity check in the confirmation UI. */
  dailyQuantity?: number;
}

export interface ParsedPrescription {
  ok: true;
  input: string;
  /** The substring that was actually recognised — highlight this in the UI. */
  matched: string;
  draft: PrescriptionDraft;
  foodRelation: FoodRelation;
  durationDays?: number;
  /** Strength written into the same line — `500` from `GLYCOMET 500MG`. */
  strength?: string;
  /** Its unit, normalised: `GM`, `GMS` and `GRM` all become `g`. */
  strengthUnit?: string;
  /**
   * What a dose is measured in, when the line says — `ml` from `5ML BD`.
   *
   * Distinct from `strengthUnit`: a tablet has a strength AND a count,
   * a syrup has only a volume, and calling five millilitres a strength
   * leaves the dose reading as "1".
   */
  doseUnit?: string;
  /** `medium` means we guessed something the prescriber did not state. */
  confidence: 'high' | 'medium';
  /** English, for logs and the consoles. The phone reads `notices`. */
  warnings: string[];
  /**
   * The same warnings as codes — RX-I18N-08.
   *
   * The phone showed `warnings` on the confirmation panel, in English, to a
   * reader in any of nine languages. A code and its values let each client
   * say it in the reader's own language; `warnings[i]` and `notices[i]`
   * always describe the same thing.
   */
  notices: NotationNotice[];
}

export type NotationNotice =
  | { code: 'no_food_instruction' }
  | { code: 'stat_once' }
  | { code: 'as_needed' }
  | { code: 'uneven_interval'; values: { hours: string } }
  | { code: 'read_as_slots' }
  | { code: 'read_as_frequency'; values: { frequency: FrequencyCode } };

export type FrequencyCode =
  | 'four_daily' | 'three_daily' | 'twice_daily' | 'once_daily'
  | 'at_bedtime' | 'at_night' | 'in_morning' | 'in_evening';

/**
 * Every label in LATIN_FREQUENCIES, as a code. Exhaustive on purpose: a label
 * missing here would have to fall back to SOME frequency, and any fallback is
 * a false statement about a dose on the panel that exists to catch those.
 */
const FREQUENCY_CODE: Record<string, FrequencyCode> = {
  'once daily': 'once_daily',
  'four times daily': 'four_daily',
  'three times daily': 'three_daily',
  'twice daily': 'twice_daily',
  'at bedtime': 'at_bedtime',
  'at night': 'at_night',
  'in the morning': 'in_morning',
  'in the evening': 'in_evening',
};

export interface ParseFailure {
  ok: false;
  input: string;
  reason: string;
}

export type ParseResult = ParsedPrescription | ParseFailure;

/**
 * Where a dose sits relative to the meal it is anchored to. Conservative
 * defaults drawn from common dispensing advice; the user can adjust per dose.
 */
export const FOOD_OFFSET_MINUTES: Record<FoodRelation, number> = {
  before_food: -30,
  with_food: 0,
  after_food: 30,
  empty_stomach: -60,
  any: 0,
};

const THREE_SLOT_MEALS: readonly MealKey[] = ['breakfast', 'lunch', 'dinner'];
const FOUR_SLOT_MEALS: readonly MealKey[] = ['breakfast', 'lunch', 'dinner', 'bedtime'];

/** Longest-first so that `TDS` is not shadowed by `TD`, `BDS` not by `BD`. */
const LATIN_FREQUENCIES: ReadonlyArray<readonly [RegExp, MealKey[], string]> = [
  [/\bQ\.?D\.?S\.?\b/i, [...FOUR_SLOT_MEALS], 'four times daily'],
  [/\bQ\.?I\.?D\.?\b/i, [...FOUR_SLOT_MEALS], 'four times daily'],
  [/\bT\.?D\.?S\.?\b/i, [...THREE_SLOT_MEALS], 'three times daily'],
  [/\bT\.?I\.?D\.?\b/i, [...THREE_SLOT_MEALS], 'three times daily'],
  [/\bB\.?D\.?S\.?\b/i, ['breakfast', 'dinner'], 'twice daily'],
  [/\bB\.?I\.?D\.?\b/i, ['breakfast', 'dinner'], 'twice daily'],
  [/\bB\.?D\.?\b/i, ['breakfast', 'dinner'], 'twice daily'],
  [/\bQ\.?H\.?S\.?\b/i, ['bedtime'], 'at bedtime'],
  [/\bH\.?S\.?\b/i, ['bedtime'], 'at bedtime'],
  [/\bNOCTE\b|\bO\.?N\.?\b/i, ['bedtime'], 'at night'],
  [/\bMANE\b|\bO\.?M\.?\b|\bQ\.?A\.?M\.?\b/i, ['breakfast'], 'in the morning'],
  [/\bQ\.?P\.?M\.?\b/i, ['dinner'], 'in the evening'],
  [/\bO\.?D\.?\b/i, ['breakfast'], 'once daily'],
  [/\bQ\.?D\.?\b/i, ['breakfast'], 'once daily'],
  [/\bS\.?I\.?D\.?\b/i, ['breakfast'], 'once daily'],
];

const FOOD_MARKERS: ReadonlyArray<readonly [RegExp, FoodRelation]> = [
  [/\bEMPTY\s*STOMACH\b|\bE\s*\/?\s*S\b|\bNIL\s*BY\s*MOUTH\b/i, 'empty_stomach'],
  [/\bAFTER\s*(FOOD|MEAL|MEALS)\b|\bA\s*\/\s*F\b|\bP\s*\/?\s*C\b|\bP\.C\.\b/i, 'after_food'],
  [/\bBEFORE\s*(FOOD|MEAL|MEALS)\b|\bB\s*\/\s*F\b|\bA\s*\/\s*C\b|\bA\.C\.\b/i, 'before_food'],
  [/\bWITH\s*(FOOD|MEAL|MEALS)\b|\bW\s*\/\s*F\b/i, 'with_food'],
];

const PRN = /\bS\.?O\.?S\.?\b|\bP\.?R\.?N\.?\b|\bAS\s*(NEEDED|REQUIRED)\b/i;
const STAT = /\bSTAT\b/i;
const EVERY_N_HOURS = /\bQ\.?\s*(\d{1,2})\s*\.?H\b|\bEVERY\s+(\d{1,2})\s*(?:HRS?|HOURS?)\b/i;
/** Three or four dash-separated quantities: `1-0-1`, `1/2-0-1`, `1-1-1-1`. */
const SLOTS = /(?<![\d/.])((?:\d+(?:\.\d+)?|\d+\s*\/\s*\d+|[½¼¾])(?:\s*[-–—]\s*(?:\d+(?:\.\d+)?|\d+\s*\/\s*\d+|[½¼¾])){2,3})(?![\d/.])/;
const DURATION = /\b(?:x|for)\s*(\d{1,3})\s*(?:d|days?)\b/i;

/**
 * The same three or four quantities, separated by spaces or commas.
 *
 * `1 1 1` and `1,0,1` are written as often as `1-0-1`, and a phone
 * keyboard makes the dash the hardest of the three to type. The old
 * parser refused all of them.
 *
 * Deliberately stricter than the dashed form: each slot must be a single
 * digit or a simple fraction. Without that, `12 10 2026` parses as a
 * dose of two thousand and twenty-six, and an OCR'd line like
 * `GLYCOMET 500 1 0 1` picks up the strength as a slot. A dash is
 * explicit enough to trust with larger numbers; a space is not.
 */
const SPACED_SLOTS =
  /(?<![\d/.\-–—])((?:\d(?:\.\d)?|\d\s*\/\s*\d|[½¼¾])(?:[ \t,]+(?:\d(?:\.\d)?|\d\s*\/\s*\d|[½¼¾])){2,3})(?![\d/.\-–—])/;

/**
 * Strength written into the same line — `GLYCOMET 500MG 1-0-1`.
 *
 * Pulled out and REMOVED before the slots are read, which is the point:
 * left in, `500 MG 1 0 1` gives the spaced matcher a fourth number to
 * chew on. Returned as well, because the OCR path has to fill the
 * strength field from somewhere and it is written right there.
 */
const STRENGTH = /\b(\d+(?:\.\d+)?)\s*(MCG|MG|GM?S?|GRM|KG|IU|UNITS?|%)\b/i;

/**
 * A volume is the DOSE, not the strength — found by probing `SYP 5ML BD`.
 *
 * For a tablet, `500MG` is how strong it is and `1` is how many you
 * take. For a syrup there is no separate count: `5ML` IS the dose. The
 * first version treated every amount alike, stripped `5ML` out with the
 * strengths, and left the quantity defaulting to 1 — so the panel said
 * "1 syrup at breakfast" for an instruction that means five millilitres.
 *
 * So these are matched but NOT removed, which lets `leadingQuantity`
 * read the 5 as the quantity exactly as it already does for `2 tab BD`.
 */
const DOSE_VOLUME = /\b(\d+(?:\.\d+)?)\s*(ML|MLS|L|TSP|TSPS|TEASPOONS?|TBSP|DROPS?|PUFFS?|SACHETS?)\b/i;

const VOLUME_SPELLING: Record<string, string> = {
  ML: 'ml', MLS: 'ml', L: 'l',
  TSP: 'spoon', TSPS: 'spoon', TEASPOON: 'spoon', TEASPOONS: 'spoon',
  TBSP: 'spoon', DROP: 'drop', DROPS: 'drop',
  PUFF: 'puff', PUFFS: 'puff', SACHET: 'sachet', SACHETS: 'sachet',
};

/** Normalised so `GM`, `GMS` and `GRM` do not become three different units. */
const UNIT_SPELLING: Record<string, string> = {
  MCG: 'mcg', MG: 'mg', G: 'g', GM: 'g', GMS: 'g', GS: 'g', GRM: 'g',
  KG: 'kg', ML: 'ml', L: 'l', IU: 'IU', UNIT: 'units', UNITS: 'units', '%': '%',
};

/**
 * "One after breakfast" — RX-MED-04.
 *
 * Meal-specific instructions, which the slot form cannot say: `1-0-1`
 * fixes a dose to morning and night but says nothing about whether it
 * goes before or after that particular meal, and prescriptions routinely
 * differ per dose.
 *
 * ── What is deliberately NOT here ─────────────────────────────────────────
 * `BD` and `AD` for "before/after dinner". `BD` is *bis die* — twice a
 * day — one of the commonest abbreviations in Indian prescribing, and
 * already parsed as such. Reading it as "before dinner" would halve
 * somebody's dose silently. Dinner takes the full words only, and there
 * is a test that says so.
 */
const MEAL_PHRASES: ReadonlyArray<readonly [RegExp, MealKey, 'before' | 'after']> = [
  /*
   * `(?<![A-Za-z])` rather than `\b`, because `1abf` is written without
   * the space and there is no word boundary between a digit and a
   * letter. The trailing guard is what keeps `BL` out of `BLOOD` and
   * `TABLET`, which a bare `\b` on the left would not have done.
   */
  [/(?<![A-Za-z])B\.?B\.?F\.?(?![A-Za-z])|\bBEFORE\s+BREAKFAST\b/i, 'breakfast', 'before'],
  [/(?<![A-Za-z])A\.?B\.?F\.?(?![A-Za-z])|\bAFTER\s+BREAKFAST\b/i, 'breakfast', 'after'],
  [/(?<![A-Za-z])B\.?L\.?(?![A-Za-z])|\bBEFORE\s+LUNCH\b/i, 'lunch', 'before'],
  [/(?<![A-Za-z])A\.?L\.?(?![A-Za-z])|\bAFTER\s+LUNCH\b/i, 'lunch', 'after'],
  [/\bBEFORE\s+DINNER\b/i, 'dinner', 'before'],
  [/\bAFTER\s+DINNER\b/i, 'dinner', 'after'],
  [/\bBEFORE\s+BED(?:\s*TIME)?\b/i, 'bedtime', 'before'],
];

/**
 * The part of the day, in words — "1 morning, 1 night".
 *
 * How somebody types it when they are not copying shorthand off a slip.
 * Mapped to the same four meal anchors the rest of the parser uses, so
 * "morning" still means this person's own breakfast time rather than a
 * fixed 08:00.
 */
const TIME_WORDS: ReadonlyArray<readonly [RegExp, MealKey]> = [
  [/\bMORNING\b/i, 'breakfast'],
  [/\bNOON\b|\bAFTERNOON\b|\bMIDDAY\b/i, 'lunch'],
  [/\bEVENING\b/i, 'dinner'],
  [/\bNIGHT\b|\bBED\s*TIME\b/i, 'bedtime'],
];

/** "twice a day", "3 times daily", "once per day". */
const WORD_FREQUENCY = [
  /\b(ONCE|TWICE|THRICE)\s+(?:A\s+|PER\s+|EVERY\s+|EACH\s+)?(?:DAY|DAILY)\b/i,
  /\b(\d+|ONE|TWO|THREE|FOUR)\s*TIMES?\s+(?:A\s+|PER\s+|EVERY\s+|EACH\s+)?(?:DAY|DAILY)\b/i,
] as const;

const WORD_COUNT: Record<string, number> = {
  ONCE: 1, ONE: 1, '1': 1,
  TWICE: 2, TWO: 2, '2': 2,
  THRICE: 3, THREE: 3, '3': 3,
  FOUR: 4, '4': 4,
};

const MEALS_FOR_COUNT: Record<number, readonly MealKey[]> = {
  1: ['breakfast'],
  2: ['breakfast', 'dinner'],
  3: THREE_SLOT_MEALS,
  4: FOUR_SLOT_MEALS,
};

const FREQUENCY_FOR_COUNT: Record<number, FrequencyCode> = {
  1: 'once_daily', 2: 'twice_daily', 3: 'three_daily', 4: 'four_daily',
};

/** Parse prescription shorthand. Returns a draft the user must confirm. */
export function parsePrescriptionNotation(input: string): ParseResult {
  const text = input.trim();
  if (!text) return { ok: false, input, reason: 'Empty input' };

  const warnings: string[] = [];
  const notices: NotationNotice[] = [];
  /** One warning, in both forms, so the two lists can never drift apart. */
  const warn = (english: string, notice: NotationNotice) => {
    warnings.push(english);
    notices.push(notice);
  };
  let confidence: 'high' | 'medium' = 'high';

  // Food relation is orthogonal to frequency; pull it out first.
  let foodRelation: FoodRelation = 'any';
  for (const [re, relation] of FOOD_MARKERS) {
    if (re.test(text)) {
      foodRelation = relation;
      break;
    }
  }
  if (foodRelation === 'any') {
    warn('No food instruction found — defaulting to "any time".', { code: 'no_food_instruction' });
    confidence = 'medium';
  }

  const durationMatch = DURATION.exec(text);
  const durationDays = durationMatch ? Number(durationMatch[1]) : undefined;

  /*
   * Strength out first, and out of the way.
   *
   * Removing it is the point rather than a side effect: left in,
   * `GLYCOMET 500 MG 1 0 1` hands the spaced-slot matcher a fourth
   * number and it reads the strength as a dose.
   */
  const volumeMatch = DOSE_VOLUME.exec(text);
  const doseUnit = volumeMatch
    ? (VOLUME_SPELLING[volumeMatch[2]!.toUpperCase()] ?? volumeMatch[2]!.toLowerCase())
    : undefined;
  const strengthMatch = STRENGTH.exec(text);
  const strength = strengthMatch ? strengthMatch[1]! : undefined;
  const strengthUnit = strengthMatch
    ? (UNIT_SPELLING[strengthMatch[2]!.toUpperCase()] ?? strengthMatch[2]!.toLowerCase())
    : undefined;
  const body = strengthMatch ? text.replace(strengthMatch[0], ' ') : text;

  const base = {
    ok: true as const,
    input,
    foodRelation,
    ...(durationDays !== undefined ? { durationDays } : {}),
    ...(strength !== undefined ? { strength } : {}),
    ...(strengthUnit !== undefined ? { strengthUnit } : {}),
    ...(doseUnit !== undefined ? { doseUnit } : {}),
  };

  /*
   * The food warning is added up front, before we know which branch
   * will answer. A branch that states the timing per dose — "1 after
   * breakfast" — has not omitted anything, so it takes it back rather
   * than telling somebody their explicit instruction is missing.
   */
  const dropFoodWarning = () => {
    const i = notices.findIndex((n) => n.code === 'no_food_instruction');
    if (i >= 0) {
      notices.splice(i, 1);
      warnings.splice(i, 1);
    }
  };

  if (STAT.test(text)) {
    warn('The prescription says STAT: a single dose, given at once. DrBell sets no reminder for it. If it should repeat, ask the prescriber and add it as written.', { code: 'stat_once' });
    return {
      ...base,
      matched: 'STAT',
      draft: { type: 'prn' },
      confidence: 'medium',
      warnings,
      notices,
    };
  }

  if (PRN.test(text)) {
    const m = PRN.exec(text)!;
    warn('As-needed medication — DrBell will not alarm for this.', { code: 'as_needed' });
    return { ...base, matched: m[0], draft: { type: 'prn' }, confidence: 'high', warnings, notices };
  }

  const hourly = EVERY_N_HOURS.exec(text);
  if (hourly) {
    const hours = Number(hourly[1] ?? hourly[2]);
    if (!(hours > 0) || hours > 24) {
      return { ok: false, input, reason: `"every ${hours} hours" is not a usable interval` };
    }
    if (24 % hours !== 0) {
      warn(
        `Every ${hours} hours does not divide evenly into a day, so dose times will drift daily.`,
        { code: 'uneven_interval', values: { hours: String(hours) } },
      );
    }
    return {
      ...base,
      matched: hourly[0],
      draft: { type: 'interval', intervalHours: hours },
      confidence: 'high',
      warnings,
      notices,
    };
  }

  // `1-0-1` style. Preferred over Latin abbreviations because it carries
  // per-slot quantities.
  const slotMatch = SLOTS.exec(body) ?? SPACED_SLOTS.exec(body);
  if (slotMatch) {
    const parts = slotMatch[1]!.split(/[-–—,\s]+/).filter(Boolean).map((p) => parseQuantity(p.trim()));
    if (parts.some((p) => p === null)) {
      return { ok: false, input, reason: `Could not read quantities in "${slotMatch[1]}"` };
    }
    const quantities = parts as number[];
    const meals = quantities.length === 4 ? FOUR_SLOT_MEALS : THREE_SLOT_MEALS;
    if (quantities.length === 4) {
      warn('Read as morning / afternoon / evening / night.', { code: 'read_as_slots' });
    }
    const anchors = buildAnchors(
      quantities.map((q, i) => [meals[i]!, q] as const).filter(([, q]) => q > 0),
      foodRelation,
    );
    if (anchors.length === 0) {
      return { ok: false, input, reason: 'All doses in the notation are zero' };
    }
    return {
      ...base,
      matched: slotMatch[1]!,
      draft: {
        type: 'meal_relative',
        anchors,
        dailyQuantity: quantities.reduce((a, b) => a + b, 0),
      },
      confidence,
      warnings,
      notices,
    };
  }

  /*
   * Meal-specific instructions: `1 ABF`, `2 before dinner`.
   *
   * Scanned for ALL matches rather than the first, because a
   * prescription routinely differs per dose — "1 after breakfast and 2
   * after dinner" is one instruction, not two medicines. Each anchor
   * carries its own before/after, which is exactly what the slot form
   * cannot express.
   */
  const mealHits: MealAnchor[] = [];
  const mealMatched: string[] = [];
  for (const [re, meal, when] of MEAL_PHRASES) {
    const m = re.exec(body);
    if (!m) continue;
    const qty = leadingQuantity(body, m.index) ?? 1;
    mealHits.push({
      meal,
      offsetMinutes: meal === 'bedtime' ? 0 : FOOD_OFFSET_MINUTES[when === 'before' ? 'before_food' : 'after_food'],
      doseQuantity: qty,
    });
    mealMatched.push(m[0]);
  }
  if (mealHits.length > 0) {
    // The instruction said when, per dose. Nothing was omitted.
    dropFoodWarning();
    return {
      ...base,
      matched: mealMatched.join(' '),
      draft: {
        type: 'meal_relative',
        anchors: mealHits,
        dailyQuantity: mealHits.reduce((a, b) => a + (b.doseQuantity ?? 1), 0),
      },
      confidence: 'high',
      warnings,
      notices,
    };
  }

  /*
   * The part of the day in words — "1 morning, 1 night".
   *
   * How somebody types it when they are not copying shorthand. Mapped
   * onto the same meal anchors, so "morning" is still this person's own
   * breakfast rather than a fixed 08:00.
   */
  const timeHits: MealAnchor[] = [];
  const timeMatched: string[] = [];
  for (const [re, meal] of TIME_WORDS) {
    const m = re.exec(body);
    if (!m) continue;
    const qty = leadingQuantity(body, m.index) ?? 1;
    timeHits.push({
      meal,
      offsetMinutes: meal === 'bedtime' ? 0 : FOOD_OFFSET_MINUTES[foodRelation],
      doseQuantity: qty,
    });
    timeMatched.push(m[0]);
  }
  if (timeHits.length > 0) {
    return {
      ...base,
      matched: timeMatched.join(' '),
      draft: {
        type: 'meal_relative',
        anchors: timeHits,
        dailyQuantity: timeHits.reduce((a, b) => a + (b.doseQuantity ?? 1), 0),
      },
      confidence: 'high',
      warnings,
      notices,
    };
  }

  /*
   * "Twice a day", "3 times daily".
   *
   * Spread over the same meal slots the Latin abbreviations use, and
   * flagged `medium` for the same reason: the prescriber said how often,
   * not when, so the times are ours and the person should check them.
   */
  for (const re of WORD_FREQUENCY) {
    const m = re.exec(body);
    if (!m) continue;
    const count = WORD_COUNT[m[1]!.toUpperCase()];
    if (!count) {
      return {
        ok: false,
        input,
        reason: `"${m[0]}" is more doses a day than DrBell can place at meal times — use "every N hours" instead`,
      };
    }
    const meals = MEALS_FOR_COUNT[count]!;
    const qty = leadingQuantity(body, m.index) ?? 1;
    warn(`Read as ${count} a day using your meal times — adjust if the timing differs.`, {
      code: 'read_as_frequency',
      values: { frequency: FREQUENCY_FOR_COUNT[count]! },
    });
    return {
      ...base,
      matched: m[0],
      draft: {
        type: 'meal_relative',
        anchors: buildAnchors(meals.map((meal) => [meal, qty] as const), foodRelation),
        dailyQuantity: qty * meals.length,
      },
      confidence: 'medium',
      warnings,
      notices,
    };
  }

  for (const [re, meals, label] of LATIN_FREQUENCIES) {
    const m = re.exec(body);
    if (!m) continue;
    // A leading quantity: "2 BD" = two tablets twice a day.
    const qty = leadingQuantity(body, m.index) ?? 1;
    const anchors = buildAnchors(
      meals.map((meal) => [meal, qty] as const),
      foodRelation,
    );
    warn(`Read as "${label}" using your meal times — adjust if the timing differs.`, {
      code: 'read_as_frequency',
      values: { frequency: FREQUENCY_CODE[label]! },
    });
    return {
      ...base,
      matched: m[0],
      draft: {
        type: 'meal_relative',
        anchors,
        dailyQuantity: qty * meals.length,
      },
      confidence: 'medium',
      warnings,
      notices,
    };
  }

  return {
    ok: false,
    input,
    reason: 'No recognisable dosing pattern (try "1-0-1", "1 1 1", "1 ABF", "twice a day", "BD" or "Q6H")',
  };
}

function buildAnchors(
  slots: ReadonlyArray<readonly [MealKey, number]>,
  foodRelation: FoodRelation,
): MealAnchor[] {
  return slots.map(([meal, doseQuantity]) => ({
    meal,
    // "After food" is meaningless for a bedtime dose; anchor it to bedtime itself.
    offsetMinutes: meal === 'bedtime' ? 0 : FOOD_OFFSET_MINUTES[foodRelation],
    doseQuantity,
  }));
}

/** Accepts `1`, `0.5`, `1/2` and the vulgar fractions that OCR tends to emit. */
export function parseQuantity(raw: string): number | null {
  const s = raw.replace(/\s+/g, '');
  if (s === '½') return 0.5;
  if (s === '¼') return 0.25;
  if (s === '¾') return 0.75;
  const frac = /^(\d+)\/(\d+)$/.exec(s);
  if (frac) {
    const denominator = Number(frac[2]);
    if (denominator === 0) return null;
    return Number(frac[1]) / denominator;
  }
  if (/^\d+(\.\d+)?$/.test(s)) return Number(s);
  return null;
}

/**
 * The number preceding an abbreviation, past any unit word.
 *
 * "2 BD" is two tablets twice a day, and so is "2 tablets twice a day" —
 * but in the second the digit is not adjacent to what matched, so a
 * strict look-behind read it as one. Getting this wrong halves or
 * doubles a dose while the panel shows a confident sentence about it.
 */
const TRAILING_UNIT =
  /\s*(?:TABLETS?|TABS?|CAPSULES?|CAPS?|PILLS?|TSPS?|TEASPOONS?|TBSP|SPOONS?|DROPS?|PUFFS?|SACHETS?|ML|UNITS?)\.?$/i;

function leadingQuantity(text: string, index: number): number | null {
  const before = text.slice(0, index).trimEnd().replace(TRAILING_UNIT, '');
  const m = /(\d+(?:\.\d+)?|\d+\s*\/\s*\d+|[½¼¾])$/.exec(before.trimEnd());
  return m ? parseQuantity(m[1]!) : null;
}

/**
 * Where an "every N hours" medicine starts its day. Fixed, and now said —
 * B2C audit: every app sent 08:00 and no screen mentioned it, so "every 8
 * hours" rang at 8, 4 and midnight with nobody having been told midnight.
 */
export const INTERVAL_ANCHOR = '08:00';

/** The clock times an "every N hours" medicine rings each day, from the anchor. */
export function intervalTimes(hours: number, anchor = INTERVAL_ANCHOR): string[] {
  if (!(hours > 0) || hours > 24) return [];
  const [h, m] = anchor.split(':').map(Number) as [number, number];
  const out: string[] = [];
  for (let t = h * 60 + m, n = 0; n < Math.floor(24 / hours); n++, t += hours * 60) {
    const mins = t % 1440;
    out.push(`${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`);
  }
  return out;
}

/** A plain-language echo of a parsed draft, for the confirmation screen. */
export function describeDraft(parsed: ParsedPrescription, unit = 'tablet'): string {
  const { draft, foodRelation } = parsed;
  if (draft.type === 'prn') return `${unit} as needed — no reminders`;
  if (draft.type === 'interval') {
    const times = intervalTimes(draft.intervalHours!);
    return `1 ${unit} every ${draft.intervalHours} hours${times.length > 1 ? `, at ${times.join(', ')}` : ''}`;
  }

  /*
   * Each dose says when it actually rings — found in the care-home
   * walkthrough. This read "1 tablet at breakfast, 1 tablet at dinner after
   * food": "after food" hung off the last dose only, and "at breakfast" was
   * not when the reminder rings (30 minutes after). It is the sentence a
   * nurse checks against the slip, so it has to be exact, the way the phone
   * already lists the doses.
   */
  const timing = (a: { meal: string; offsetMinutes: number }): string =>
    a.offsetMinutes > 0
      ? `${a.offsetMinutes} min after ${a.meal}`
      : a.offsetMinutes < 0
        ? `${-a.offsetMinutes} min before ${a.meal}`
        : a.meal === 'bedtime'
          ? 'at bedtime'
          : `with ${a.meal}`;
  const parts = (draft.anchors ?? []).map((a) => `${a.doseQuantity ?? 1} ${unit} ${timing(a)}`);
  const stomach = foodRelation === 'empty_stomach' ? ', on an empty stomach' : '';
  return parts.join(', ') + stomach;
}

/**
 * The reverse: what somebody picked, written as the shorthand for it.
 *
 * The parser is for people copying a slip. Plenty of people cannot —
 * the slip is in a script they do not read, or the handwriting defeats
 * them, or they simply do not know what `TDS` means. Those people pick
 * the slots on a screen instead, and this turns that back into the
 * notation.
 *
 * Emitting the notation rather than an opaque draft buys three things:
 * the medicines list can show `1-0-1 A/F` for a manually built schedule
 * exactly as it does for a typed one; the schedule row's
 * `sourceNotation` stays meaningful, so a later change can re-parse it;
 * and the person sees the shorthand for what they chose, which is how
 * they learn to type it next time.
 *
 * `notationRoundTrips` in the tests asserts the property that makes all
 * three safe: parsing what this produces gives back what went in.
 */
export interface BuiltSlots {
  breakfast?: number;
  lunch?: number;
  dinner?: number;
  bedtime?: number;
}

const FOOD_SUFFIX: Record<FoodRelation, string> = {
  after_food: ' A/F',
  before_food: ' B/F',
  with_food: ' W/F',
  empty_stomach: ' E/S',
  any: '',
};

/** `0.5` as `1/2`, because that is how a prescription writes it. */
function writeQuantity(n: number): string {
  if (n === 0) return '0';
  if (n === 0.5) return '1/2';
  if (n === 0.25) return '1/4';
  if (n === 0.75) return '3/4';
  if (Number.isInteger(n)) return String(n);
  return String(n);
}

export function buildNotation(slots: BuiltSlots, foodRelation: FoodRelation = 'any'): string {
  const b = slots.breakfast ?? 0;
  const l = slots.lunch ?? 0;
  const d = slots.dinner ?? 0;
  const n = slots.bedtime ?? 0;
  /*
   * Four slots only when the fourth is used. `1-0-1-0` is a legal thing
   * to write but nobody writes it, and the three-slot form is what a
   * reader recognises.
   */
  const parts = n > 0 ? [b, l, d, n] : [b, l, d];
  return parts.map(writeQuantity).join('-') + FOOD_SUFFIX[foodRelation];
}
